import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { BadgeComponent } from '@shared/components/badge/badge.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { FileUploadComponent } from '@shared/components/file-upload/file-upload.component';
import { LangSwitcherComponent } from '@shared/components/lang-switcher/lang-switcher.component';
import { PhoneComponent } from '@shared/components/phone/phone.component';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { SharedModule } from '@shared/modules/shared.module';
import { PhoneFormatPipe } from '@shared/pipes/phone-format/phone-format.pipe';
import { ConfirmationService } from 'primeng/api';
import { UiCodeBlockComponent } from '../ui-code-block/ui-code-block.component';

@Component({
	selector: 'app-ui-others',
	imports: [
		SharedModule,
		FileUploadComponent,
		LangSwitcherComponent,
		BadgeComponent,
		AvatarComponent,
		ButtonComponent,
		ConfirmDialogComponent,
		TooltipComponent,
		UiCodeBlockComponent,
		PhoneComponent,
		PhoneFormatPipe,
	],
	providers: [ConfirmationService],
	templateUrl: './ui-others.component.html',
	styleUrl: './ui-others.component.scss',
})
export class UiOthersComponent {
	fileUploadControl = new FormControl<File[]>([]);
	phoneControl = new FormControl<string | null>(null);
	phoneDisplayValue = '+34|612345678';

	constructor(private confirmationService: ConfirmationService) {}

	showConfirmDialog(): void {
		this.confirmationService.confirm({
			header: '¿Estás seguro?',
			message: '¿Deseas continuar con esta acción?',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: 'Aceptar',
			rejectLabel: 'Cancelar',
			accept: () => {
				// Action confirmed
			},
			reject: () => {
				// Action cancelled
			},
		});
	}

	showDeleteConfirmDialog(): void {
		this.confirmationService.confirm({
			header: 'Confirmar eliminación',
			message: '¿Estás seguro de que deseas eliminar este elemento? Esta acción no se puede deshacer.',
			icon: 'pi pi-trash',
			acceptLabel: 'Eliminar',
			rejectLabel: 'Cancelar',
			acceptButtonStyleClass: 'p-button-danger',
			accept: () => {
				// Element deleted
			},
			reject: () => {
				// Deletion cancelled
			},
		});
	}

	generateCode(attrs: {
		component: string;
		// File upload
		formControl?: string;
		placeholder?: string;
		// Language switcher
		isFloating?: boolean;
		// Badge
		value?: string | number;
		badgeSize?: string;
		severity?: string;
		// Button
		text?: string;
		route?: string;
		disabled?: boolean;
		type?: string;
		size?: string;
		// Avatar
		imageUrl?: string;
		name?: string;
		extraInfo?: string;
		// Confirm dialog
		methodName?: string;
		// Tooltip
		tooltipText?: string;
		tooltipPosition?: string;
		showDelay?: number;
		hideDelay?: number;
	}): string {
		let code = '';

		if (attrs.component === 'file-upload') {
			code = '<app-file-upload\n';
			if (attrs.formControl) code += `    [formControl]="${attrs.formControl}"\n`;
			if (attrs.placeholder) code += `    placeholder="${attrs.placeholder}"\n`;
			code += '/>';
		} else if (attrs.component === 'lang-switcher') {
			code = '<app-lang-switcher\n';
			if (attrs.isFloating !== undefined) code += `    [isFloating]="${attrs.isFloating}"\n`;
			code += '/>';
		} else if (attrs.component === 'badge') {
			code = '<app-badge\n';
			if (attrs.value) code += `    value="${attrs.value}"\n`;
			if (attrs.badgeSize) code += `    badgeSize="${attrs.badgeSize}"\n`;
			if (attrs.severity) code += `    severity="${attrs.severity}"\n`;
			code += '/>';
		} else if (attrs.component === 'avatar') {
			code = '<app-avatar\n';
			if (attrs.imageUrl) code += `    imageUrl="${attrs.imageUrl}"\n`;
			if (attrs.name) code += `    name="${attrs.name}"\n`;
			if (attrs.extraInfo) code += `    extraInfo="${attrs.extraInfo}"\n`;
			code += '/>';
		} else if (attrs.component === 'confirm-dialog') {
			code = '// In your HTML template:\n';
			code += '<app-confirm-dialog />\n\n';
			code += '// In your TypeScript component:\n';
			code += 'constructor(private confirmationService: ConfirmationService) {}\n\n';
			if (attrs.methodName) {
				code += `${attrs.methodName}(): void {\n`;
				code += '  this.confirmationService.confirm({\n';
				code += '    header: "¿Estás seguro?",\n';
				code += '    message: "¿Deseas continuar con esta acción?",\n';
				code += '    icon: "pi pi-exclamation-triangle",\n';
				code += '    acceptLabel: "Aceptar",\n';
				code += '    rejectLabel: "Cancelar",\n';
				code += '    accept: () => { /* Your logic here */ },\n';
				code += '    reject: () => { /* Your logic here */ }\n';
				code += '  });\n';
				code += '}';
			}
		} else if (attrs.component === 'tooltip') {
			code = '<app-tooltip\n';
			if (attrs.tooltipText) code += `    tooltipText="${attrs.tooltipText}"\n`;
			if (attrs.tooltipPosition) code += `    [tooltipPosition]="'${attrs.tooltipPosition}'"\n`;
			if (attrs.showDelay) code += `    [showDelay]="${attrs.showDelay}"\n`;
			if (attrs.hideDelay) code += `    [hideDelay]="${attrs.hideDelay}"\n`;
			code += '>\n    <!-- Your content here -->\n</app-tooltip>';
		} else if (attrs.component === 'password-tooltip') {
			code = '<app-password\n';
			code += '    placeholder="Contraseña"\n';
			code += '    [showTooltip]="true"\n';
			code += '    [error]="true"\n';
			code += '    errorMessage="Contraseña inválida"\n';
			code += '/>\n\n';
			code += '// Password requirements:\n';
			code += '// - 8 caracteres mínimo\n';
			code += '// - Una mayúscula\n';
			code += '// - Una minúscula\n';
			code += '// - Un número\n';
			code += '// - Un carácter especial (!@#$%^&*)';
		} else if (attrs.component === 'phone') {
			code = '<app-phone\n';
			code += '    [formControl]="phoneControl"\n';
			code += '    label="Teléfono"\n';
			code += '    placeholder="000 000 000"\n';
			code += '/>';
		} else if (attrs.component === 'phone-format-pipe') {
			code = '// En tu template:\n';
			code += '{{ phoneValue | phoneFormat }}\n\n';
			code += '// Ejemplo:\n';
			code += '// Input: "+34|612345678"\n';
			code += '// Output: "+34 612 345 678"';
		}

		return code;
	}
}
